import { CheckCircle2, Clock, Package, XCircle } from "lucide-react";

import { Card, CardContent } from "~/components/ui/card";
import { cn } from "~/lib/utils";
import type { ProductionBatchStatus } from "~/types/production";

interface BatchStatisticsCardsProps {
  stats: {
    total: number;
    planned: number;
    in_progress: number;
    completed: number;
    cancelled: number;
  };
  activeStatus?: string;
  onStatusClick?: (status: ProductionBatchStatus | "all") => void;
}

export function BatchStatisticsCards({
  stats,
  activeStatus,
  onStatusClick,
}: BatchStatisticsCardsProps) {
  const cards: {
    title: string;
    value: number;
    icon: typeof Package;
    color: string;
    status: ProductionBatchStatus;
  }[] = [
    {
      title: "Planificados",
      value: stats.planned,
      icon: Package,
      color: "text-yellow-600 bg-yellow-100",
      status: "planned",
    },
    {
      title: "En Progreso",
      value: stats.in_progress,
      icon: Clock,
      color: "text-blue-600 bg-blue-100",
      status: "in_progress",
    },
    {
      title: "Completados",
      value: stats.completed,
      icon: CheckCircle2,
      color: "text-green-600 bg-green-100",
      status: "completed",
    },
    {
      title: "Cancelados",
      value: stats.cancelled,
      icon: XCircle,
      color: "text-gray-600 bg-gray-100",
      status: "cancelled",
    },
  ];

  const handleClick = (status: ProductionBatchStatus) => {
    if (!onStatusClick) return;
    // Toggle: if already active, clear filter
    if (activeStatus === status) {
      onStatusClick("all");
    } else {
      onStatusClick(status);
    }
  };

  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
      {cards.map((card) => {
        const isActive = activeStatus === card.status;
        return (
          <Card
            key={card.title}
            className={cn(
              "cursor-pointer transition-all hover:shadow-md",
              isActive && "ring-2 ring-primary ring-offset-2"
            )}
            onClick={() => handleClick(card.status)}
          >
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className={`rounded-full p-2 ${card.color}`}>
                  <card.icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{card.value}</p>
                  <p className="text-sm text-muted-foreground">{card.title}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
